import { Fragment } from 'react'

import { Link } from '@twistopayments/design-system/components/atoms/link'
import {
  BulletedListItem,
  NumberedListItem,
} from '@twistopayments/design-system/components/molecules/list'
import { isNil } from 'ramda'

import type { RichTextBlock, RichTextChildren } from './rich-text.d'

type ParentInfo = {
  format?: RichTextBlock['format']
}

const getComponent = (
  type: RichTextChildren['type'],
  parentInfo?: ParentInfo
) => {
  switch (type) {
    case 'link':
      return Link
    case 'list-item':
      return parentInfo?.format === 'unordered'
        ? BulletedListItem
        : NumberedListItem
    default:
      return Fragment
  }
}

export const useRichText = () => {
  const renderChildren = (
    childrenArray: RichTextChildren[],
    parentInfo?: ParentInfo
  ) =>
    childrenArray.map((child, index) => {
      const {
        type,
        text,
        url,
        bold,
        italic,
        strikethrough,
        children,
        code,
        underline,
      } = child
      const Component = getComponent(type, parentInfo)

      const props: Record<string, unknown> = {}
      if (type === 'link') {
        props.href = url
      }

      let content = <>{text}</>
      if (bold === true) {
        content = <strong>{content}</strong>
      }

      if (italic === true) {
        content = <em>{content}</em>
      }

      if (strikethrough === true) {
        content = <s>{content}</s>
      }

      if (underline === true) {
        content = <u>{content}</u>
      }

      if (code === true) {
        content = <code>{content}</code>
      }

      return (
        <Component key={index} {...props}>
          {!isNil(children) ? renderChildren(children) : content}
        </Component>
      )
    })

  return {
    renderChildren,
  }
}
